import { CommonModule } from "@angular/common";
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	ContentChild,
	input,
	model,
	output,
	signal,
	TemplateRef,
	ViewChild,
	ViewEncapsulation,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { AutoComplete, AutoCompleteModule } from "primeng/autocomplete";

export type OptionGroup<T> = {
	value: string;
	label: string;
	items: T[];
};

@Component({
	selector: "ic-page-filter-control",
	imports: [AutoCompleteModule, CommonModule, FormsModule],
	templateUrl: "./page-filter-control.component.html",
	styleUrl: "./page-filter-control.component.scss",
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class PageFilterControlComponent<T extends { id: string }> {
	/** Used to display the selected option from `valueOptions`. */
	readonly selectedOptionLabel = input<(option: T) => string>();

	/** The suggestions list to display (filtered by the parent component) */
	readonly suggestions = input<(T | OptionGroup<T>)[]>([]);

	/** Use when options for the suggestions list (`valueOptions`) are grouped. */
	readonly groupItemsKey = input<string>();

	/** The property that determines option disabling in the suggestions list. */
	readonly optionDisabledProperty = input<string>();

	/** Displayed in the textual search input whenever the control value is empty. */
	readonly placeholder = input<string>();

	/** The properties to search by when suggestions get filtered. */
	readonly inputId = input.required<string>();

	/** Suggestions list overlay gets a fixed width applied. Set to 'large' if suggestions lack horizontal space. */
	readonly suggestionsListWidth = input<"medium" | "large">("medium");

	/** Whether the control is in readonly mode. */
	readonly isReadonly = input<boolean>(false);

	/** The selected option from the list. */
	readonly selectedOption = input<T | null>(null);

	/** Whether the loading spinner should be displayed. */
	readonly showLoadingSpinner = input<boolean>(false);

	@ViewChild("filterControl") filterControl: AutoComplete;

	/**
	 * Used to customise the appearance of the options in the suggestions list. The option is available as a context
	 * variable.
	 */
	@ContentChild("optionTemplate") optionTemplate: TemplateRef<any>;

	/**
	 * Used to prefix the selected option display with custom content. The selected option is available as a context
	 * variable.
	 */
	@ContentChild("selectedOptionPrefixTemplate") selectedOptionPrefixTemplate: TemplateRef<any>;

	query = model<string>("");
	selectedId = output<string | undefined>();

	protected readonly suggestionsListHeight = "480px";
	private isSelectionInProgress = signal(false);
	private postSelectionGuard = false;

	protected readonly filterControlModel = computed(() => (this.isSelectionInProgress() ? null : this.selectedOption()));

	protected onValueChange(value: string | undefined): void {
		this.selectedId.emit(value);
		this.isSelectionInProgress.set(true);
		this.postSelectionGuard = true;
		setTimeout(() => (this.postSelectionGuard = false));
	}

	protected onFilterControlFocus(): void {
		const previousIsSelectionInProgress = this.isSelectionInProgress();
		this.isSelectionInProgress.set(true);
		if (!this.isReadonly() && previousIsSelectionInProgress === false) {
			if (this.postSelectionGuard) {
				this.postSelectionGuard = false;
				this.isSelectionInProgress.set(false);
				this.filterControl.inputEL?.nativeElement.blur();
			} else {
				this.query.set("");

				// Trigger the suggestions dropdown to show
				this.filterControl.show();
			}
		} else {
			this.isSelectionInProgress.set(false);
			this.filterControl.inputEL.nativeElement.blur();
		}
	}

	protected onFilterControlBlur(): void {
		this.isSelectionInProgress.set(false);
	}

	protected onCompleteMethod(event: { query: string }): void {
		this.query.set(event.query);
	}
}
